import { CardMetricScope, identifiersNamedBy, metricIdentitiesForCard } from './metricIdentifiers';

/**
 * PLA-8815: the catalogue's `Show Confidence Score?` column for Layer 1, and the card gate derived
 * from it.
 *
 * `Calculation & Governance Rules` -> *Confidence display eligibility* governs both:
 *
 *   Yes           calculate/display the metric's Result Confidence, and include it in the
 *                 standard Card Confidence.
 *   No            do not calculate/display it, and exclude it from the aggregation.
 *   Metric Itself the metric's own VALUE is a confidence measure -- so it neither carries a Result
 *                 Confidence of its own (confidence-on-confidence) nor contributes one to its card.
 *                 Two Layer 1 metrics are in this class: O-COM-005 and O-HYB-010.
 *
 * and the rule that decides a CARD: *"If a card has no applicable Yes metrics, do not calculate a
 * standard Card Confidence solely from No/Metric Itself metrics."*
 *
 * Transcribed from the `Metric List` sheet of `Perx 3.0 - Layer 2 Phase 1 Metrics Catalogue.xlsx`
 * (all 117 `Screen = Overall Intelligence` rows -- 71 `Yes`, 44 `No`, 2 `Metric Itself` -- grouped
 * by that sheet's own Card Name so a row can be checked against it), and GENERATED from the
 * workbook rather than hand-typed. Never inferred from the payload: a metric answering with the
 * five confidence columns says the backend computed something, not that the catalogue lets its card
 * show it. O-COM-024 and O-COM-025 both carry a live `confidence_score` on UAT `generic`
 * (0.9274 / 0.59, 2026-09-08) and both are `Yes` here; O-HYB-004 carries one too (its column set
 * declares the five) and is `Yes`, while O-HYB-005 declares none and is `No` -- the two facts are
 * independent and only this table decides eligibility.
 *
 * The 36 `O-*-PR-*` Product Readiness ids are all `Yes`. They are the per-product metrics
 * `PRODUCT_READINESS_METRIC_IDS` stamps onto the card at render time, so the card is eligible
 * whichever product the dropdown selects.
 */
export type ConfidenceDisplayRule = 'yes' | 'no' | 'metricItself';

export const CUSTOMER_INSIGHTS_CONFIDENCE_DISPLAY: Record<string, ConfidenceDisplayRule> = {
  // Engagement Funnel
  'O-COM-013': 'no',

  // Customer Behaviour Trends
  'O-COM-007': 'yes',
  'O-COM-008': 'yes',
  'O-COM-009': 'yes',
  'O-COM-010': 'yes',

  // Customer Engagement
  'O-COM-011': 'no',
  'O-COM-012': 'no',

  // Customer Lifecycle & Product Holdings
  'O-COM-031': 'no',
  'O-COM-032': 'no',

  // Customer Population & Activity
  'O-COM-001': 'no',
  'O-COM-002': 'no',

  // Data Trust & Integration Health
  'O-COM-003': 'no',
  'O-COM-004': 'no',
  'O-COM-005': 'metricItself',
  'O-COM-006': 'no',
  'O-REV-008': 'no',

  // Current & Predicted Customer Value
  'O-COM-028': 'yes',
  'O-COM-029': 'yes',

  // Customer Sentiment & Expectations
  'O-COM-020': 'yes',
  'O-COM-021': 'yes',
  'O-REV-010': 'no',

  // Contact Restrictions
  'O-COM-027': 'no',

  // Growth Opportunities & Product Interest
  'O-COM-024': 'yes',
  'O-COM-025': 'yes',

  // Growth Planner Handoff Readiness
  'O-COM-030': 'no',

  // Ready for Engagement
  'O-COM-026': 'yes',

  // Rewards & Loyalty
  'O-COM-015': 'yes',
  'O-COM-016': 'yes',
  'O-COM-017': 'no',
  'O-COM-018': 'no',
  'O-COM-019': 'no',
  'O-COM-033': 'no',
  'O-REV-001': 'no',
  'O-REV-002': 'no',
  'O-REV-003': 'no',
  'O-REV-004': 'no',
  'O-REV-005': 'no',
  'O-REV-006': 'no',
  'O-REV-007': 'no',

  // Risk & Unusual Activity
  'O-COM-022': 'no',
  'O-COM-023': 'yes',
  'O-REV-009': 'no',

  // Debt & Spending Health
  'O-BFSI-005': 'no',
  'O-BFSI-006': 'no',
  'O-BFSI-007': 'no',

  // Income & Cashflow Health
  'O-BFSI-001': 'yes',
  'O-BFSI-002': 'yes',
  'O-BFSI-003': 'yes',
  'O-BFSI-004': 'yes',

  // Product Readiness
  'O-BFS-PR-001': 'yes',
  'O-BFS-PR-002': 'yes',
  'O-BFS-PR-003': 'yes',
  'O-BFS-PR-004': 'yes',
  'O-BFS-PR-005': 'yes',
  'O-BFS-PR-006': 'yes',
  'O-BFS-PR-007': 'yes',
  'O-BFS-PR-008': 'yes',
  'O-BFS-PR-009': 'yes',
  'O-FIN-PR-001': 'yes',
  'O-FIN-PR-002': 'yes',
  'O-FIN-PR-003': 'yes',
  'O-FIN-PR-004': 'yes',
  'O-FIN-PR-005': 'yes',
  'O-FIN-PR-006': 'yes',
  'O-FIN-PR-007': 'yes',
  'O-FIN-PR-008': 'yes',
  'O-FIN-PR-009': 'yes',
  'O-INS-PR-001': 'yes',
  'O-INS-PR-002': 'yes',
  'O-INS-PR-003': 'yes',
  'O-INS-PR-004': 'yes',
  'O-INS-PR-005': 'yes',
  'O-INS-PR-006': 'yes',
  'O-INS-PR-007': 'yes',
  'O-INS-PR-008': 'yes',
  'O-INS-PR-009': 'yes',
  'O-INS-PR-010': 'yes',
  'O-TEL-PR-001': 'yes',
  'O-TEL-PR-002': 'yes',
  'O-TEL-PR-003': 'yes',
  'O-TEL-PR-004': 'yes',
  'O-TEL-PR-005': 'yes',
  'O-TEL-PR-006': 'yes',
  'O-TEL-PR-007': 'yes',
  'O-TEL-PR-008': 'yes',

  // Merchant & Category Data Quality
  'O-HYB-001': 'yes',
  'O-HYB-002': 'yes',
  'O-HYB-003': 'yes',
  'O-HYB-004': 'yes',
  'O-HYB-010': 'metricItself',
  'O-HYB-011': 'no',
  'O-HYB-012': 'no',

  // Merchant & Category Preferences
  'O-HYB-013': 'yes',
  'O-HYB-014': 'no',
  'O-HYB-015': 'no',

  // Payment Behaviour & Cash Usage
  'O-HYB-005': 'no',
  'O-HYB-006': 'yes',
  'O-HYB-007': 'yes',
  'O-HYB-008': 'yes',
  'O-HYB-009': 'no',

  // Payment Usage & Risk
  'O-FIN-003': 'no',
  'O-FIN-005': 'yes',

  // Wallet Activity & Funding
  'O-FIN-001': 'no',
  'O-FIN-002': 'no',
  'O-FIN-004': 'no',

  // Policy Health & Claims
  'O-INS-001': 'no',
  'O-INS-002': 'yes',
  'O-INS-004': 'no',

  // Renewal & Growth Opportunities
  'O-INS-003': 'yes',
  'O-INS-005': 'yes',
  'O-INS-006': 'yes',

  // Churn & Service Experience
  'O-TEL-005': 'yes',
  'O-TEL-006': 'yes',

  // Plan Usage & Revenue
  'O-TEL-001': 'no',
  'O-TEL-002': 'no',

  // Recharge & Upgrade Readiness
  'O-TEL-003': 'yes',
  'O-TEL-004': 'yes'
};

/** The catalogue's rule for one identifier, or `undefined` for an id the sheet does not carry. */
export const confidenceDisplayRuleFor = (identifier: string): ConfidenceDisplayRule | undefined =>
  Object.prototype.hasOwnProperty.call(CUSTOMER_INSIGHTS_CONFIDENCE_DISPLAY, identifier)
    ? CUSTOMER_INSIGHTS_CONFIDENCE_DISPLAY[identifier]
    : undefined;

/**
 * `yes` only.
 *
 * `metricItself` is excluded for a DIFFERENT reason than `no` -- the reading is already a confidence
 * -- and an unknown identifier is excluded too, because a card may not be scored on the strength of
 * a metric whose eligibility this file cannot state.
 */
export const contributesToCardConfidence = (identifier: string): boolean =>
  confidenceDisplayRuleFor(identifier) === 'yes';

/**
 * Whether this card may draw a Card Confidence pill at all.
 *
 * Derived from the card's OWN declared metrics rather than hand-passed per section, so a card whose
 * catalogue metrics are all `No`/`Metric Itself` can never draw one.
 *
 * `identifiersNamedBy` rather than the `metric` kind alone, so a `ratio`/`difference`/`derived`/
 * `distribution` tile is judged on the catalogue metrics it actually reads. An `unmapped` tile names
 * none and therefore makes no card eligible.
 *
 * `metricIdentitiesForCard` and a `scope`, because Product Readiness resolves its metric at RENDER
 * time: its four static tiles are all `unmapped`, and only once the band dropdown names a product
 * does the card read an `O-*-PR-*` id. All 36 are `Yes`, so that card is eligible with a product
 * selected and — correctly — not eligible without one, since with no product it reads no catalogue
 * metric at all.
 */
export const customerInsightsCardShowsConfidence = (
  cardKey: string,
  scope?: CardMetricScope
): boolean =>
  metricIdentitiesForCard(cardKey, scope)
    .flatMap(entry => identifiersNamedBy(entry.source))
    .some(contributesToCardConfidence);
